/* Product page rails (sections/related-products.liquid): "Complete the look" and "You may also like" from ONE request
   to Shopify's product recommendations (Section Rendering API through the recommendations URL, related intent), sent
   when the rails come within about 600 px of the screen.
   The answer is one track of grid cards (available pieces first) plus each piece's type. Pieces of this product's
   type go to "You may also like", the others to "Complete the look" (unless the product's own picks already fill it;
   those aren't repeated), keeping the answer's order, up to data-max each. A rail with fewer than 2 pieces is hidden,
   and so is the whole block when neither has any (or the request fails). When fewer than 2 pieces of other types come
   back (and none are hand-picked), "Complete the look" asks for sections/look-pairs instead (data-pairs-url): up to 6
   pieces from the collections paired with this type in theme settings. Nothing is added to the address bar or the
   history; quick add in the new cards is handled by the global assets/quick-add.js. */
if (!customElements.get('lmb-related')) {
  const MIN_CARDS = 2;
  /* Pieces shown when "Complete the look" falls back to the types paired in theme settings. */
  const PAIRS_MAX = 6;

  /* The product handle a card links to (/products/<handle>, with or without a language prefix or tracking query). */
  const handleOf = (cell) => {
    const link = cell.querySelector('a[href]');
    if (!link) return '';
    try {
      const path = new URL(link.getAttribute('href'), window.location.origin).pathname;
      return decodeURIComponent(path.split('/').filter(Boolean).pop() || '');
    } catch (error) {
      return '';
    }
  };

  customElements.define(
    'lmb-related',
    class LmbRelated extends HTMLElement {
      connectedCallback() {
        // Moved in the page while still waiting: watch again; once loading has started, nothing more to do.
        if (this.dataset.state && this.dataset.state !== 'waiting') return;
        // Only the hand-picked rail is on (already filled with the page): no request at all.
        if (!this.querySelector('[data-related-rail]:not([data-related-filled])')) {
          this.dataset.state = 'loaded';
          return;
        }
        this.dataset.state = 'waiting';
        if (!('IntersectionObserver' in window)) {
          this.load();
          return;
        }
        this.observer = new IntersectionObserver(
          (entries) => {
            if (!entries.some((entry) => entry.isIntersecting)) return;
            this.observer.disconnect();
            this.load();
          },
          { rootMargin: '0px 0px 600px 0px' }
        );
        this.observer.observe(this);
      }

      disconnectedCallback() {
        if (this.observer) this.observer.disconnect();
      }

      async load() {
        this.dataset.state = 'loading';
        let cells = [];
        let types = {};
        try {
          const doc = await LMB.fetchHTML(this.dataset.url);
          const track = doc.querySelector('[data-related-answer] [data-rail-track]');
          if (track) cells = Array.from(track.children).filter((el) => el.matches('.rail__cell'));
          const map = doc.querySelector('[data-related-types]');
          if (map) types = JSON.parse(map.textContent) || {};
        } catch (error) {
          cells = [];
        }

        const look = this.querySelector('[data-related-rail="look"]');
        const like = this.querySelector('[data-related-rail="like"]');
        const lookPicked = !!(look && look.hasAttribute('data-related-filled'));
        // The hand-picked pieces already on the page aren't shown twice.
        const shown = new Set(lookPicked ? Array.from(look.querySelectorAll('.rail__cell')).map(handleOf) : []);
        // Same type as this piece: "You may also like". A product without a type can't tell, so all go there.
        const type = (this.dataset.type || '').trim();
        const groups = { look: [], like: [] };
        cells.forEach((cell) => {
          const handle = handleOf(cell);
          if (!handle || shown.has(handle)) return;
          const same = !type || types[handle] === type;
          groups[same ? 'like' : 'look'].push(cell);
        });

        this.fill(like, groups.like);
        if (!lookPicked) {
          // Too few other types from the recommendations: the pieces paired with this type in theme settings instead
          // (sections/look-pairs.liquid: in stock, closest in price, none already in the bag), up to 6.
          const pairs = groups.look.length < MIN_CARDS && this.dataset.pairsUrl ? await this.loadPairs() : null;
          if (pairs) this.fill(look, pairs, PAIRS_MAX);
          else this.fill(look, groups.look);
        }
        this.dataset.state = 'loaded';
        if (![look, like].some((rail) => rail && !rail.hidden)) this.hidden = true;
      }

      /* The paired pieces' cards, or null when the answer fails or holds fewer than 2. */
      async loadPairs() {
        try {
          const doc = await LMB.fetchHTML(this.dataset.pairsUrl);
          const track = doc.querySelector('[data-look-pairs] [data-rail-track]');
          const cells = track ? Array.from(track.children).filter((el) => el.matches('.rail__cell')) : [];
          return cells.length >= MIN_CARDS ? cells : null;
        } catch (error) {
          return null;
        }
      }

      /* Swaps the rail's empty cells for the cards, before its "See all" end card; too few cards hide the rail. */
      fill(rail, cells, limit) {
        if (!rail) return;
        const track = rail.querySelector('[data-rail-track]');
        if (!track || cells.length < MIN_CARDS) {
          rail.hidden = true;
          return;
        }
        const max = limit || Number(this.dataset.max) || 8;
        const end = track.querySelector('.rail__end');
        track.querySelectorAll('[data-rail-placeholder]').forEach((cell) => cell.remove());
        cells.slice(0, max).forEach((cell) => track.insertBefore(cell, end));
        track.removeAttribute('aria-busy');
        rail.setAttribute('data-related-filled', '');
      }
    }
  );
}
